import React, { useEffect, useRef, Component } from 'react';
import { certificationsData } from '../utils/constants';
import { ExternalLinkIcon } from 'lucide-react';
const Certifications = ({
  language = 'en'
}) => {
  const carouselRef = useRef(null);
  const sectionRef = useRef(null);
  // Animation for section entrance
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const elements = entry.target.querySelectorAll('.fade-in');
          elements.forEach((el, index) => {
            setTimeout(() => {
              el.classList.add('active');
            }, index * 100);
          });
        }
      });
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  // Create duplicate certificates for continuous scrolling
  const duplicatedCertifications = [...certificationsData, ...certificationsData];
  // Translations
  const translations = {
    en: {
      title: 'Professional',
      titleHighlight: 'Certifications',
      description: 'Continuous professional development through industry-recognized certifications from leading financial institutions and organizations.',
      viewCertificate: 'View Certificate'
    },
    vi: {
      title: 'Chứng Chỉ',
      titleHighlight: 'Chuyên Nghiệp',
      description: 'Phát triển chuyên môn liên tục thông qua các chứng chỉ được công nhận từ các tổ chức tài chính và tổ chức hàng đầu.',
      viewCertificate: 'Xem Chứng Chỉ'
    }
  };
  const t = translations[language];
  return <section id="certifications" ref={sectionRef} className="w-full py-24 bg-background" data-id="element-249">
      <div className="container mx-auto px-4" data-id="element-250">
        <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-16 fade-in" data-id="element-251">
          {t.title} <span className="gradient-text" data-id="element-252">{t.titleHighlight}</span>
        </h2>
        <p className="text-muted text-center mb-12 max-w-3xl mx-auto fade-in" data-id="element-253">
          {t.description}
        </p>
        {/* Mobile Carousel (Continuous Scroll) */}
        <div className="md:hidden overflow-hidden" data-id="element-254">
          <div className="flex animate-[scroll_25s_linear_infinite] hover:pause-animation" style={{
          width: `${certificationsData.length * 320}px`
        }} ref={carouselRef} data-id="element-255">
            {duplicatedCertifications.map((cert, index) => <div key={`mobile-${cert.name}-${index}`} className="w-[280px] mx-4 flex-shrink-0" data-id="element-256">
                <CertificationItem certification={cert} viewText={t.viewCertificate} language={language} data-id="element-257" />
              </div>)}
          </div>
        </div>
        {/* Tablet/Desktop Carousel (Continuous Scroll) */}
        <div className="hidden md:block overflow-hidden" data-id="element-258">
          <div className="flex animate-[scroll_35s_linear_infinite] hover:pause-animation" style={{
          width: `${certificationsData.length * 400}px`
        }} ref={carouselRef} data-id="element-259">
            {duplicatedCertifications.map((cert, index) => <div key={`desktop-${cert.name}-${index}`} className="w-[350px] mx-6 flex-shrink-0" data-id="element-260">
                <CertificationItem certification={cert} viewText={t.viewCertificate} language={language} data-id="element-261" />
              </div>)}
          </div>
        </div>
        <style jsx data-id="element-262">{`
          @keyframes scroll {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }
          .hover\\:pause-animation:hover {
            animation-play-state: paused;
          }
        `}</style>
      </div>
    </section>;
};
// Certification Item Component
const CertificationItem = ({
  certification,
  viewText,
  language = 'en'
}) => {
  return <a href={certification.link} target="_blank" rel="noopener noreferrer" className="block" data-id="element-263">
      <div className="bg-secondary rounded-xl overflow-hidden card-glow h-full transition-all duration-300 transform hover:scale-105 hover:rotate-1 group" data-id="element-264">
        <div className="h-32 bg-white flex items-center justify-center p-4" data-id="element-265">
          <img src={certification.logo} alt={language === 'en' ? certification.organization : certification.organizationVi} className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-110" data-id="element-266" />
        </div>
        <div className="p-5" data-id="element-267">
          <h3 className="text-lg font-display font-bold mb-1 group-hover:text-accent-cyan transition-colors" data-id="element-268">
            {language === 'en' ? certification.name : certification.nameVi}
          </h3>
          <p className="text-accent-cyan text-sm mb-2" data-id="element-269">
            {language === 'en' ? certification.organization : certification.organizationVi}
          </p>
          <p className="text-muted text-sm mb-3 line-clamp-2" data-id="element-270">
            {language === 'en' ? certification.description : certification.descriptionVi}
          </p>
          <div className="flex items-center text-primary/70 text-sm group-hover:text-accent-cyan transition-colors" data-id="element-271">
            <span data-id="element-272">{viewText}</span>
            <ExternalLinkIcon size={14} className="ml-1 group-hover:translate-x-1 transition-transform" data-id="element-273" />
          </div>
        </div>
      </div>
    </a>;
};
export default Certifications;